<template>
  <div v-if="imageData" class="image-wrapper">
    <img
      :src="processedImageUrl"
      :alt="isDecorative ? '' : altText"
      :aria-hidden="isDecorative ? 'true' : undefined"
      @error="handleImageError"
      :class="{ 'opacity-0': isLoading || hasError }"
    />
    <div
      v-if="isLoading"
      class="absolute inset-0 flex items-center justify-center rounded-md rounded-tl-none bg-background-07"
    >
      <div class="animate-spin text-brand-02">
        <BaseIcon icon="LoaderCircle" />
      </div>
    </div>
    <div v-if="hasError" class="absolute inset-0 flex items-center justify-center bg-background-07">
      <div class="text-states-error">
        <BaseIcon icon="ImageOff" />
      </div>
    </div>
    <p v-if="imageData.caption" class="caption">{{ imageData.caption }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { IBaseImage } from './IBaseImage'
import BaseIcon from '@/common/components/base/BaseIcon/BaseIcon.vue'

const props = defineProps<IBaseImage>()

const isLoading = ref(true)
const hasError = ref(false)

// Computed properties
const processedImageUrl = computed(() => {
  if (!props.imageData) return ''

  const url = props.imageData.image.src
  const params = new URLSearchParams()

  // Add size parameters if available
  if (props.editorConfig?.size) {
    params.append('width', props.editorConfig.size.width.toString())
    params.append('height', props.editorConfig.size.height.toString())
  }

  //Add cropping parameters if available
  if (props.imageData.coordinates) {
    params.append('cropAlias', 'default')
    params.append('x1', props.imageData.coordinates.x1.toString())
    params.append('y1', props.imageData.coordinates.y1.toString())
    params.append('x2', props.imageData.coordinates.x2.toString())
    params.append('y2', props.imageData.coordinates.y2.toString())
  } else if (props.imageData.focalPoint) {
    params.append('focalPointTop', props.imageData.focalPoint.top.toString())
    params.append('focalPointLeft', props.imageData.focalPoint.left.toString())
  } else if (props.editorConfig?.size) {
    // Default focal point if needed
    params.append('focalPointTop', '0.5')
    params.append('focalPointLeft', '0.5')
  }

  return `${url}?${params.toString()}`
})

const altText = computed(() => {
  if (props.imageData.image.alt) return props.imageData.image.alt
  if (!props.imageData.caption) return '' // Laat leeg als er geen caption is
  return props.imageData.caption // Fallback alleen als er geen specifieke altText is
})

// Handle image load events
const handleImageLoad = () => {
  isLoading.value = false
}

// Handle image error events
const handleImageError = () => {
  isLoading.value = false
  hasError.value = true
}

// Preload the image
if (props.imageData) {
  const img = new Image()
  img.onload = handleImageLoad
  img.onerror = handleImageError
  img.src = processedImageUrl.value
}
</script>

<style scoped>
.image-wrapper {
  @apply relative flex max-w-full flex-col items-center justify-center overflow-hidden;
}

.caption {
  @apply mt-1 text-xs text-text-dark;
}

img {
  @apply block h-auto w-full max-w-full object-cover object-center transition-opacity duration-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-03;
}
</style>
